import { useState, useEffect, useCallback } from "react";
import { theme } from "styles/theme";

type DeviceType = "mobile" | "tablet" | "laptop" | "desktop";

interface ResponsiveData {
  isMobile: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  deviceType: DeviceType;
}

/**
 * Debounce function to limit execution frequency
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;

  return function (...args: Parameters<T>): void {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/**
 * Hook to manage responsiveness performantly
 * @returns an object with information about the current device type
 */
export const useResponsive = (): ResponsiveData => {
  // Convert theme breakpoints to numbers
  const breakpoints = {
    mobile: parseInt(theme.breakpoints.mobile),
    tablet: parseInt(theme.breakpoints.tablet),
    laptop: parseInt(theme.breakpoints.laptop),
    desktop: parseInt(theme.breakpoints.desktop),
  };

  // Function to determine device type based on screen width
  const getDeviceType = useCallback((): DeviceType => {
    const width = window.innerWidth;
    if (width < breakpoints.tablet) return "mobile";
    if (width < breakpoints.laptop) return "tablet";
    if (width < breakpoints.desktop) return "laptop";
    return "desktop";
  }, [breakpoints.tablet, breakpoints.laptop, breakpoints.desktop]);

  // Function to get responsive data based on device type
  const getResponsiveData = useCallback((): ResponsiveData => {
    const deviceType = getDeviceType();
    return {
      isMobile: deviceType === "mobile",
      isTablet: deviceType === "tablet",
      isLaptop: deviceType === "laptop",
      isDesktop: deviceType === "desktop",
      deviceType,
    };
  }, [getDeviceType]);

  const [responsive, setResponsive] = useState<ResponsiveData>(
    getResponsiveData()
  );

  useEffect(() => {
    // Function to update state with new dimensions
    const handleResize = debounce(() => {
      setResponsive(getResponsiveData());
    }, 200);

    // Sets initial state
    setResponsive(getResponsiveData());

    // Add listener for window resize event with debounce
    window.addEventListener("resize", handleResize);

    // Clean up listener when component is unmounted
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [getResponsiveData]);

  return responsive;
};
